import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  MenuItem,
  Select,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useSelector } from "react-redux";

// Icons from Material UI Icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  validateName,
  validateEmail,
  validatePhone,
} from "../../../../utils/validation";

function OrderReceiver({
  receiverData,
  setReceiverData,
  forceOpen
}) {
  const authUser = useSelector((state) => state.UserProfile?.allData);

  const [expanded, setExpanded] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+971",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // Get receiver details from localStorage
  const getReceiverDetailsLocal = () => {
    try {
      const userId = authUser?.id;

      if (!userId) {
        return {
          name: "",
          email: "",
          countryCode: "+971",
          phone: "",
        };
      }

      const storedData = localStorage.getItem("receiverDetails");
      let allUsers = {};

      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData && typeof parsedData === "object" && !Array.isArray(parsedData)) {
            allUsers = parsedData;
          }
        } catch (error) {
          console.error("Invalid receiverDetails localStorage data");
        }
      }

      const userData = allUsers[userId] || {};

      return {
        // Always from authUser
        name: authUser?.firstName || "",
        email: authUser?.email || "",
        // From localStorage
        countryCode: userData.countryCode || "+971",
        phone: userData.phone || "",
      };
    } catch (error) {
      console.error("Failed to get receiver details:", error);
      return {
        name: authUser?.firstName || "",
        email: authUser?.email || "",
        countryCode: "+971",
        phone: "",
      };
    }
  };

  // Save receiver details to localStorage
  const saveReceiverDetailsLocal = ({ countryCode, phone }) => {
    try {
      const userId = authUser?.id;

      if (!userId) {
        return false;
      }

      const storedData = localStorage.getItem("receiverDetails");
      let allUsers = {};

      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData && typeof parsedData === "object" && !Array.isArray(parsedData)) {
            allUsers = parsedData;
          }
        } catch (error) {
          console.error("Invalid receiverDetails localStorage data");
        }
      }

      allUsers[userId] = {
        countryCode: countryCode || "+971",
        phone: phone || "",
      };

      localStorage.setItem("receiverDetails", JSON.stringify(allUsers));
      return true;
    } catch (error) {
      console.error("Failed to save receiver details:", error);
      return false;
    }
  };

  // Load data when authUser changes
  useEffect(() => {
    if (authUser?.id) {
      const data = getReceiverDetailsLocal();
      setFormData(data);
      setReceiverData({
        name: data.name,
        email: data.email,
        phoneNumber: data.phone ? `${data.countryCode}${data.phone}` : "",
      });
    }
  }, [authUser]);

  // Handle accordion expand
  useEffect(() => {
    if (forceOpen) {
      setExpanded(true);
    }
  }, [forceOpen]);

  // Handle phone change only (name & email are read-only)
  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const formattedValue = value.replace(/\D/g, "").slice(0, 9);

    setFormData((prev) => {
      const updated = {
        ...prev,
        phone: formattedValue,
      };

      // Save to localStorage on phone change
      if (authUser?.id) {
        saveReceiverDetailsLocal({
          countryCode: updated.countryCode,
          phone: formattedValue,
        });
      }

      return updated;
    });
  };

  const handleCountryCodeChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      countryCode: value,
    }));
  };

  // Validate and update receiverData
  useEffect(() => {
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone, formData.countryCode),
    };

    setErrors(newErrors);

    setReceiverData({
      name: formData.name?.trim() || "",
      email: formData.email?.trim() || "",
      phoneNumber: formData.phone
        ? `${formData.countryCode}${formData.phone}`
        : "",
    });
  }, [formData]);

  const inputBlockStyle = (hasError = false) => ({
    borderRadius: "12px",
    border: hasError ? "2px solid #cb0202" : "1px solid #E4C18F",
    transition: "all 0.3s ease-in-out",
    "& .MuiOutlinedInput-root": {
      "& fieldset": { border: "none" },
    },
  });

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      disableGutters
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: { xs: '100%', md: 600, lg: 650, xl: 700 },
        "@media (min-width:360px) and (max-width:399px)": { maxWidth: 345 },
        "@media (min-width:400px) and (max-width:479px)": { maxWidth: 380 },
        "@media (min-width:480px) and (max-width:499px)": { maxWidth: 450 },
        "@media (min-width:500px) and (max-width:539px)": { maxWidth: 480 },
        "@media (min-width:540px) and (max-width:559px)": { maxWidth: 500 },
        "@media (min-width:560px) and (max-width:599px)": { maxWidth: 520 },
        mx: "auto",
        mt: 2,
        backgroundColor: "transparent",
        borderRadius: "12px",
        border: "1.8px solid #E4C18F",
        "&:before": { display: "none" },
        "&:first-of-type": { borderRadius: { xs: "12px", sm: "20px" } },
        "&:last-of-type": { borderRadius: { xs: "12px", sm: "20px" } },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1,
          cursor: "pointer",
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "0 !important",
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: { xs: 500, sm: 600 },
            color: "primary.main",
            fontSize: { xs: "13.5px", sm: "13px", md: "14px", lg: "16px" },
            fontFamily: "Be Vietnam Pro",
          }}
        >
          Order Receiver Details
          <Box
            component="span"
            sx={{
              color: "error.main",
              verticalAlign: "super",
              fontSize: "0.9rem",
              ml: 0.2,
              fontWeight: 600
            }}
          >
            *
          </Box>
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 }, pt: 0 }}>
        <Grid container spacing={2.5}>
          {/* Name - Read Only */}
          <Grid item xs={12}>
            <Box sx={inputBlockStyle(Boolean(errors.name))}>
              <TextField
                fullWidth
                disabled
                error={Boolean(errors.name)}
                placeholder="Recipient Name"
                name="name"
                value={formData.name}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "Be Vietnam Pro",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: errors.name ? "#8B0000" : "inherit",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#1c1c1c",
                    color: "#1c1c1c",
                  },
                  "& input::placeholder": {
                    color: errors.name ? "#8B0000" : "inherit",
                    opacity: 1,
                    fontFamily: "Be Vietnam Pro",
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon
                        sx={{
                          color: errors.name ? "#8B0000" : "#1c1c1c",
                          fontSize: 26,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Phone - Editable */}
          <Grid item xs={12}>
            <Box sx={inputBlockStyle(Boolean(errors.phone))}>
              <TextField
                fullWidth
                error={Boolean(errors.phone)}
                onChange={handlePhoneChange}
                placeholder={errors.phone ? "* Required field" : "Recipient Mobile"}
                name="phone"
                value={formData.phone}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "Be Vietnam Pro",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: errors.phone ? "#8B0000" : "inherit",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#1c1c1c",
                    color: "#1c1c1c",
                  },
                  "& .MuiSelect-select": {
                    p: 0,
                    pr: "20px !important",
                    fontFamily: "Be Vietnam Pro",
                  },
                  "& input::placeholder": {
                    color: errors.phone ? "#8B0000" : "inherit",
                    opacity: 1,
                    fontFamily: "Be Vietnam Pro",
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ gap: 1 }}>
                      <Select
                        value={formData?.countryCode}
                        name="countryCode"
                        onChange={handleCountryCodeChange}
                        variant="standard"
                        disableUnderline
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontWeight: 600,
                          fontSize: "1rem",
                          color: errors.phone ? "#8B0000" : "#1c1c1c",
                          "& .MuiSelect-select": {
                            p: 0,
                            pr: "12px !important",
                            fontFamily: "Be Vietnam Pro",
                          },
                          "&.Mui-disabled": {
                            color: "#1c1c1c",
                            WebkitTextFillColor: "#1c1c1c",
                          },
                        }}
                      >
                        <MenuItem value="+971">+971</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                  style: {
                    fontSize: "1rem",
                    fontWeight: 500,
                    fontFamily: "Be Vietnam Pro",
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Email - Read Only */}
          <Grid item xs={12}>
            <Box sx={inputBlockStyle(Boolean(errors.email))}>
              <TextField
                fullWidth
                disabled
                error={Boolean(errors.email)}
                placeholder="Recipient Email"
                name="email"
                type="email"
                value={formData.email}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "Be Vietnam Pro",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: errors.email ? "#8B0000" : "inherit",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#1c1c1c",
                    color: "#1c1c1c",
                  },
                  "& input::placeholder": {
                    color: errors.email ? "#8B0000" : "inherit",
                    opacity: 1,
                    fontFamily: "Be Vietnam Pro",
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon
                        sx={{
                          color: errors.email ? "#8B0000" : "#1c1c1c",
                          fontSize: 24,
                        }}
                      />
                    </InputAdornment>
                  ),
                  style: {
                    fontSize: "1rem",
                    fontWeight: 500,
                    fontFamily: "Be Vietnam Pro",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

export default OrderReceiver;
